import { useState, type FormEvent } from "react";

import { api, errorMessage } from "../api/client";
import logo from "../assets/elezaby-logo.png";
import { ui } from "../lib/ui";
import type { User } from "../types";

/** The hub's front door. Accounts are created by an admin (or the first one
 *  from the server's environment); there is no sign-up and no reset by
 *  email — an admin sets a new password from the server. */
export function Login({ onSignedIn }: { onSignedIn: (user: User) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSignedIn(await api.login(username, password));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <form onSubmit={submit} className={`${ui.card} flex w-full max-w-sm flex-col gap-4 p-6`}>
        <div className="flex items-center gap-3.5">
          <span className="inline-flex items-center dark:rounded-lg dark:bg-white dark:px-2 dark:py-1">
            <img src={logo} alt="El Ezaby Pharmacy" className="h-8 w-auto" />
          </span>
          <div className="border-l border-border pl-3.5 leading-tight">
            <div className="font-semibold">Retail Analytics</div>
            <div className="text-xs text-muted">Hub · sign in</div>
          </div>
        </div>
        {error && <p className={`${ui.notice} ${ui.noticeError} mb-0`}>{error}</p>}
        <label className={ui.field}>
          Username
          <input
            className={ui.input}
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            autoFocus
          />
        </label>
        <label className={ui.field}>
          Password
          <input
            className={ui.input}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        <button type="submit" className={ui.btn} disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
